import _get from 'lodash/get'
import _isFunction from 'lodash/isFunction'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _startsWith from 'lodash/startsWith'
import { parse as parseUrl, format as formatUrl } from 'url'
import { Map, List } from 'immutable'
import {
  stringify as stringifyQuery,
  parse as parseQuery,
} from '../query-string/index'
import {
  isFullUrl,
  isAnonymousWordPressContent,
  isShop,
  isWordPressSharePage,
} from '../url/index'
import { isDefault as isDefaultLanguage } from '../languages/index'
import { getUserLanguage } from '../user/index'
import { get as getConfig } from '../../config/index'

export const HISTORY_METHOD_PUSH = 'push'
export const HISTORY_METHOD_REPLACE = 'replace'
export const ABSOLUTE_URL_REGEX = /https?:\/\//

/**
 * Force a full-document reload of the current url. Unlike historyRedirect (soft SPA
 * navigation) this re-runs the server render, so it's the seam to reach for when
 * server-decided state must be recomputed — e.g. AI-1151 re-evaluates language-gated
 * PostHog flags at SSR after an in-session language change. Use this when the
 * destination is the url you are already on (query and hash survive); use assignPage
 * when it differs. No-op outside the browser; centralized here so it can be stubbed in
 * tests (jsdom does not implement navigation).
 */
export function reloadPage () {
  if (typeof window !== 'undefined' && window.location) {
    window.location.reload()
  }
}

/**
 * Force a full-document load of a specific url. Like reloadPage this re-runs the server
 * render — reach for it when the destination differs from the current url and
 * server-decided state must be recomputed, e.g. AI-1263 hard-navigates after sign-in so
 * the SSR PostHog bootstrap re-evaluates flags for the newly-authenticated identity
 * (the browser never evaluates flags itself). No-op outside the browser; centralized
 * here so it can be stubbed in tests (jsdom does not implement navigation).
 * @param {String} url The destination url
 */
export function assignPage (url) {
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign(url)
  }
}

const config = getConfig()
const proxyAnonymousSeries = !!_get(config, ['features', 'marketing', 'proxyAnonymousSeries'])
const proxyAnonymousPerson = !!_get(config, ['features', 'marketing', 'proxyAnonymousPerson'])
const proxyAnonymousVideo = !!_get(config, ['features', 'marketing', 'proxyAnonymousVideo'])
const proxyAnonymousGaiaPlus = !!_get(config, ['features', 'marketing', 'proxyAnonymousGaiaPlus'])

/**
 * Navigate to a path using a history redirect
 * accounting for all difference between authenticated
 * and anonymous users this is used for all programatic navigation
 * don't use history.push() directly
 * @param {Object} options The options
 * @param {Object} options.history The history singlton
 * @param {String} options.url The destination url
 * @param {import('immutable').Map|Object} [options.query] The query object
 * @param {Number} [options.timeout] A timeout to wait to send the redirect which
 * is useful for letting the state settle before changing the url
 * @param {String} [options.historyMethod] The method on the history object to use
 * i.e. HISTORY_METHOD_PUSH or HISTORY_METHOD_REPLACE
 * @param {import('immutable').Map} [options.auth] The auth immutable map
 * @param {import('immutable').Map} [options.user] The user immutable map
 * @param {Boolean} [options.isLoggedIn] Whether the user is logged in
 * @param {String[]|String} [options.language] The language(s) to apply to the url
 * @param {Function} [cb] An optional callback funcion which is called when the location
 * change has been run
 */
export function historyRedirect (options = {}, cb) {
  const {
    history, timeout = 100, historyMethod, auth = Map(), user, isLoggedIn,
  } = options
  let { url, query, language } = options
  if (!history) {
    throw new Error('The history option is required.')
  }
  if (!url) {
    throw new Error('The url option is required.')
  }
  // Convert immutable map to plain javascript object
  if (Map.isMap(query)) {
    query = query.toJS()
  }
  // Append another query
  if (!_isEmpty(query)) {
    const parsedUrl = parseUrl(url)
    const parsedQuery = parseQuery(_get(parsedUrl, 'search'))
    url = formatUrl({
      ...parsedUrl,
      search: stringifyQuery({ ...parsedQuery, ...query }),
    })
  }
  // Add prefix of / if missing
  if (!isFullUrl(url) && !_startsWith(url, '/')) {
    url = `/${url}`
  }
  if (!language && Map.isMap(user)) {
    language = getUserLanguage(user)
  }
  // Convert immutable to array to make it easier to work with.
  if (List.isList(language)) {
    language = language.toJS()
  }
  // Remove language when the user is authenticated or it is the default language
  if (language && (auth.get('jwt') || isDefaultLanguage(language))) {
    language = null
  }

  // Redirect if WP or shop redirect
  if (isAnonymousWordPressContent(url, isLoggedIn, proxyAnonymousSeries)
  || isAnonymousWordPressContent(url, isLoggedIn, proxyAnonymousPerson)
  || isAnonymousWordPressContent(url, isLoggedIn, proxyAnonymousVideo)
  || isAnonymousWordPressContent(url, isLoggedIn, proxyAnonymousGaiaPlus)
  || isWordPressSharePage(url)
  || isShop(url)) {
    global.location.assign(url)
    return
  }

  setTimeout(() => {
    // Use push or replace for the history method
    const redirectMethod = _get(history, historyMethod || HISTORY_METHOD_PUSH)
    redirectMethod(createUrl(url, language))
    if (_isFunction(cb)) {
      cb()
    }
  }, timeout)
}

/**
 * Create a URL applying language correctly
 * @param {String} url A url string
 * @param {String[]} language An array of languages
 * @returns {String} The url
 */
export function createUrl (url, language) {
  if (!language || isDefaultLanguage(language)) {
    return url
  }
  const parsedUrl = parseUrl(url)
  const parsedQuery = parseQuery(_get(parsedUrl, 'search'))
  const queryLanguage = _get(parsedQuery, 'language')
  if (queryLanguage) {
    return url
  }
  return formatUrl({
    ...parsedUrl,
    search: stringifyQuery({ ...parsedQuery, language: createLanguageQueryParam(language) }),
  })
}

/**
 * Create a langauge array to be used in an url query
 * @param {String[]|String} language The languages
 * @returns {String[]} The languages as an array to be used in a
 * url query
 */
export function createLanguageQueryParam (language) {
  if (!language) {
    return []
  }
  return _isArray(language) ? language : [language]
}
